import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Send, MapPin, Phone, Mail, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      // Track conversion in Google Analytics
      if (window.trackContactRequest) {
        window.trackContactRequest(formData.service || 'general');
      }
    } catch (error) {
      console.error('Contact error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-[#FDFCFA]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Info */}
          <div>
            <p className="overline mb-4">{t('contact.overline')}</p>
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#1A1A2E] font-semibold mb-6">
              {t('contact.title')}
            </h2>
            <p className="text-[#4A4A5A] mb-10 leading-relaxed">
              {t('contact.subtitle')}
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl icon-gold flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-[#1A1A2E] font-medium mb-1">{t('contact.info.address.title')}</p>
                  <p className="text-[#4A4A5A] text-sm">Jalan Gootama, 22<br />Gianyar, Ubud Bali</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl icon-gold flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-[#1A1A2E] font-medium mb-1">{t('contact.info.phone.title')}</p>
                  <a href="tel:+6282123631370" className="text-[#4A4A5A] text-sm hover:text-[#C9A227] transition-colors">+62 821 2363 1370</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl icon-gold flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-[#1A1A2E] font-medium mb-1">{t('contact.info.email.title')}</p>
                  <a href="mailto:hello@balilifeconcierge.com" className="text-[#4A4A5A] text-sm hover:text-[#C9A227] transition-colors">hello@balilifeconcierge.com</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="card-light rounded-2xl p-8">
            {isSuccess ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-playfair text-[#1A1A2E] font-semibold mb-2">
                  {t('contact.success.title')}
                </h3>
                <p className="text-[#4A4A5A]">
                  {t('contact.success.message')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-[#4A4A5A] text-sm mb-2">{t('contact.form.name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#F5F3EF] border border-transparent rounded-xl px-4 py-3 text-[#1A1A2E] placeholder-[#8A8A9A] focus:outline-none focus:border-[#C9A227] focus:bg-white transition-all"
                    placeholder={t('contact.form.namePlaceholder')}
                    data-testid="contact-name"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#4A4A5A] text-sm mb-2">{t('contact.form.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#F5F3EF] border border-transparent rounded-xl px-4 py-3 text-[#1A1A2E] placeholder-[#8A8A9A] focus:outline-none focus:border-[#C9A227] focus:bg-white transition-all"
                      placeholder={t('contact.form.emailPlaceholder')}
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-[#4A4A5A] text-sm mb-2">{t('contact.form.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-[#F5F3EF] border border-transparent rounded-xl px-4 py-3 text-[#1A1A2E] placeholder-[#8A8A9A] focus:outline-none focus:border-[#C9A227] focus:bg-white transition-all"
                      placeholder={t('contact.form.phonePlaceholder')}
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#4A4A5A] text-sm mb-2">{t('contact.form.message')} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full bg-[#F5F3EF] border border-transparent rounded-xl px-4 py-3 text-[#1A1A2E] placeholder-[#8A8A9A] focus:outline-none focus:border-[#C9A227] focus:bg-white transition-all resize-none"
                    placeholder={t('contact.form.messagePlaceholder')}
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('contact.form.sending')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>{t('contact.form.send')}</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
